import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useRef, useState, useEffect } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import InternalComplaintActionRegister from "./InternalComplaintActionRegister";
import UpdateInternalComplaintDataTable from "./UpdateInternalComplaintDataTable";

interface InternalComplaintModel {
  id: number;
  image:any;
  complaintDescription: string;
  attachmentId: number;
  severity: string;
  createdBy: number;
  createdDate: string;
  updatedBy: number;
  updatedDate: string;
  customerName: string;
  createdByName: string;
  updatedByName: string;
  mobile: string;
}

interface DataTableProps {
  data: InternalComplaintModel[];
  handlepagechange: (event: any) => void;
  pageNo: number;
  pageSize: number;
  totalRecordsCount: number;
  onComplaintUpdate: () => void;
}

const InternalComplaintDataTable: React.FC<DataTableProps> = ({
  data,
  handlepagechange,
  pageNo,
  pageSize,
  totalRecordsCount,
  onComplaintUpdate,
}) => {
  const toast = useRef<Toast>(null);
  const [selectedComplaint, setSelectedComplaint] = useState<InternalComplaintModel | null>(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [localData, setLocalData] = useState<InternalComplaintModel[]>(data);
  const [showActionsModal, setShowActionsModal] = useState(false);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleUpdateSuccess = (updatedComplaint: InternalComplaintModel) => {
    const updatedData = localData.map(item => 
      item.id === updatedComplaint.id ? updatedComplaint : item
    );
    
    setLocalData(updatedData);
    setShowUpdateDialog(false);
    
    if (toast.current) {
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Complaint updated successfully',
        life: 3000
      });
    }

    if (onComplaintUpdate) {
      onComplaintUpdate();
    }
  };

  const handleActionSuccess = () => {
    setShowActionsModal(false);
    
    if (toast.current) {
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Complaint action created successfully',
        life: 3000
      });
    }

    // Refresh the complaints data if needed
    if (onComplaintUpdate) {
      onComplaintUpdate();
    }
  };

  const actionTemplate = (rowData: any) => (
    <Button
      icon="pi pi-plus-circle"
      className="p-button-rounded p-button-text p-button-info"
      onClick={() => {
        setSelectedComplaint(rowData); 
        setShowActionsModal(true);
      }}
      tooltip="Add Action"
    />
  );

  const editTemplate = (rowData: InternalComplaintModel) => (
    <Button
      icon="pi pi-pencil"
      className="p-button-rounded p-button-text p-button-success"
      onClick={() => {
        setSelectedComplaint(rowData);
        setShowUpdateDialog(true);
      }}
      tooltip="Edit Complaint"
    />
  );

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <div className="card border-noround p-2">
        <DataTable
          value={localData}
          paginator
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          first={(pageNo - 1) * pageSize}
          totalRecords={totalRecordsCount}
          rows={pageSize}
          stripedRows
          onPage={handlepagechange}
          paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
          currentPageReportTemplate="{first} to {last} of {totalRecords}"
          emptyMessage="No internal complaints found"
        >
          <Column field="id" header="Complaint ID" sortable />
          <Column field="createdByName" header="Created By" sortable />
          <Column field="mobile" header="Mobile" sortable />
          <Column 
            field="complaintDescription" 
            header="Description" 
            style={{ maxWidth: "350px", wordWrap: "break-word" }} 
          />
          <Column field="severity" header="Severity" sortable />
          {/* <Column field="attachmentId" header="Attachment ID" sortable /> */}
          <Column 
            field="createdDate" 
            header="Created Date" 
            body={(row) => formatDate(row.createdDate)} 
            sortable 
          />
          <Column field="updatedByName" header="Updated By" sortable />
          <Column 
            field="updatedDate" 
            header="Updated Date" 
            body={(row) => formatDate(row.updatedDate)} 
            sortable 
          />
          <Column header="Edit" body={editTemplate} />
          <Column header="Action" body={actionTemplate} />
        </DataTable>

        <Dialog
          visible={showUpdateDialog}
          style={{ width: "80vw" }}
          onHide={() => setShowUpdateDialog(false)}
          modal
        >
          {selectedComplaint && (
            <UpdateInternalComplaintDataTable
              isUpdate={true}
              updateComplaintInfo={selectedComplaint}
              handleOnSuccess={handleUpdateSuccess}
            />
          )}
        </Dialog>

        <Dialog
          visible={showActionsModal}
          style={{ width: "50vw", maxWidth: "90vw" }}
          breakpoints={{ '960px': '75vw', '640px': '95vw' }}
          onHide={() => setShowActionsModal(false)}
          modal
        >
          {selectedComplaint && (
            <InternalComplaintActionRegister
              isUpdate={false}
              handleOnSuccess={handleActionSuccess}
              updateComplaintInfo={selectedComplaint}
              selectedComplaint={selectedComplaint}
            />
          )}
        </Dialog>
      </div>
    </div>
  );
};

export default InternalComplaintDataTable;